import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import AgentSkillsSection from "../components/AgentSkillsSection";
import {
  Headphones,
  MessageCircle,
  Mail,
  Send,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  ArrowLeft,
  Zap,
} from "lucide-react";

const FEATURES = [
  {
    icon: <CheckCircle className="w-6 h-6 text-amber-500" />,
    title: "Auto-resolve tickets",
    description:
      "Answers common questions instantly using your knowledge base. Resolves 70-80% of tickets without human intervention.",
  },
  {
    icon: <Zap className="w-6 h-6 text-amber-500" />,
    title: "Smart escalation",
    description:
      "Detects complex issues and escalates to the right human agent with full context. Never leaves a customer hanging.",
  },
  {
    icon: <MessageCircle className="w-6 h-6 text-amber-500" />,
    title: "Multi-channel support",
    description:
      "Receives and responds on WhatsApp, Telegram, Email, and Discord. One agent, all channels.",
  },
  {
    icon: <Headphones className="w-6 h-6 text-amber-500" />,
    title: "24/7 availability",
    description:
      "Your support never sleeps. Handle peak times, after-hours, and weekends without hiring more staff.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Connect channels",
    desc: "Link your WhatsApp Business, Telegram, Email, or Discord in 2 minutes",
  },
  {
    step: "02",
    title: "Upload knowledge",
    desc: "Add your FAQ, product docs, and policies. Agent learns in minutes.",
  },
  {
    step: "03",
    title: "Set escalation rules",
    desc: "Define when to loop in a human (keywords, sentiment, priority level)",
  },
  {
    step: "04",
    title: "Go live",
    desc: "Agent starts handling tickets. Monitor and optimize from your dashboard.",
  },
];

const CHANNELS = [
  { name: "WhatsApp Business", available: true },
  { name: "Telegram", available: true },
  { name: "Email (IMAP/SMTP)", available: true },
  { name: "Discord", available: true },
  { name: "Slack", available: true },
  { name: "Zendesk", available: false },
  { name: "Intercom", available: false },
];

const FAQ_ITEMS = [
  {
    question: "What languages does it support?",
    answer:
      "English, Portuguese, Spanish, and 20+ languages via automatic translation.",
  },
  {
    question: "Can it handle refunds?",
    answer:
      "It can initiate refund workflows, but high-value refunds are escalated to humans per your rules.",
  },
  {
    question: "How does escalation work?",
    answer:
      "You set rules: keywords ('speak to manager'), sentiment (negative), or priority. The agent transfers with full context.",
  },
  {
    question: "Does it learn from new tickets?",
    answer:
      "Yes. Your knowledge base grows over time. You can also upload new docs anytime.",
  },
  {
    question: "What's the setup time?",
    answer:
      "Self-service: 5-10 minutes with our guided setup. With Setup Assist: 24-48 hours.",
  },
];

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left px-6 py-4 text-zinc-200 font-medium hover:bg-white/[0.02] transition-colors"
      >
        <span className="font-mono text-sm">{question}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-amber-500 shrink-0 ml-4" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0 ml-4" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-4 text-zinc-400 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

const TESTIMONIALS = [
  {
    quote: "We reduced first response time from 4 hours to 30 seconds. Our CSAT score went from 82% to 94% in the first month.",
    name: "Ana Costa",
    role: "Head of Support, TechFlow (SaaS)",
    metric: "4h → 30s response time",
  },
  {
    quote: "The agent handles 80% of our WhatsApp tickets automatically. Our team now focuses on complex cases only.",
    name: "Marcos Oliveira",
    role: "COO, LogiBras (Logistics)",
    metric: "80% auto-resolution",
  },
  {
    quote: "We went from 2 agents covering 12 hours to 1 agent + Max covering 24/7. Saved $3,200/month in staffing.",
    name: "Sarah Chen",
    role: "Support Lead, AppVault (Fintech)",
    metric: "$3,200/month saved",
  },
];

export default function CustomerSupport() {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead
        title={t('customer_support.ai_customer_support_agent_247_automated_support')}
        description="Deploy AI agents for customer support. Resolve tickets 24/7, answer FAQs, escalate to humans. Integrates with WhatsApp, Telegram, Email, Discord."
        url="/marketplace/customer-support"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
            { "@type": "ListItem", position: 2, name: "Marketplace", item: "https://maxemployee.com/marketplace" },
            { "@type": "ListItem", position: 3, name: "Customer Support", item: "https://maxemployee.com/marketplace/customer-support" }
            ]
          }
        ]}
      />
      <Header />

      <main className="min-h-screen bg-[#09090B]">
        {/* Section 1 — Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #f59e0b 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 border border-zinc-800 rounded-full px-4 py-1.5 mb-8">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400">
                  {t('cs_page.marketplace_badge')}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-6">
                <Headphones className="w-12 h-12 text-amber-500" />
                <h1 className="text-5xl md:text-6xl font-display font-bold text-white leading-[1.1] tracking-tight">
                  {t('cs_page.title')}
                </h1>
              </div>

              <p className="mt-4 text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
                {t('cs_page.subtitle')}
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/pricing?vertical=customer-support"
                  className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]"
                >
                  {t('cs_page.deploy_cta')}
                </Link>
                <Link
                  to="/signup?agent=customer-support"
                  className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
                >
                  {t('cs_page.activate_cta')}
                </Link>
                <a
                  href="#pricing"
                  className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-8 py-3.5 font-mono uppercase tracking-wider text-xs transition-colors"
                >
                  {t('cs_page.pricing_cta')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb + Back link */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <nav className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              <Link to="/" className="hover:text-amber-500 transition-colors">
                {t('nav.home')}
              </Link>
              <span className="mx-3">/</span>
              <Link
                to="/marketplace"
                className="hover:text-amber-500 transition-colors"
              >
                {t('marketplace_page.breadcrumb_marketplace')}
              </Link>
              <span className="mx-3">/</span>
              <span className="text-zinc-400">{t('cs_page.breadcrumb_cs')}</span>
            </nav>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-amber-500 font-mono text-xs uppercase tracking-wider transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t('cs_page.back_to_marketplace')}
            </Link>
          </div>
        </div>

        {/* Section 2 — What your agent does (feature grid) */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            {t('cs_page.features_title')}
          </h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('cs_page.features_subtitle')}
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors"
              >
                <div className="mb-4">{f.icon}</div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {t(`cs_page.feature_${i + 1}_title`)}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {t(`cs_page.feature_${i + 1}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3 — How it works (timeline) */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            {t('cs_page.how_it_works')}
          </h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('cs_page.how_it_works_subtitle')}
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((item, i) => (
              <div
                key={item.step}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors"
              >
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-500 mb-6">
                  Step {item.step}
                </div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {t(`cs_page.step_${i + 1}_title`)}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {t(`cs_page.step_${i + 1}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4 — Integrated Channels */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            {t('cs_page.channels_title')}
          </h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('cs_page.channels_subtitle')}
          </p>

          <div className="flex flex-wrap gap-4">
            {CHANNELS.map((ch) => (
              <div
                key={ch.name}
                className={`flex items-center gap-2 border rounded-lg px-5 py-3 ${
                  ch.available
                    ? "border-zinc-800 text-zinc-300"
                    : "border-zinc-800/50 text-zinc-600"
                }`}
              >
                {ch.available ? (
                  <CheckCircle className="w-4 h-4 text-amber-500" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-zinc-700" />
                )}
                <span className="text-sm font-mono">
                  {ch.name === "WhatsApp Business" ? t('cs_page.channel_whatsapp') :
                   ch.name === "Telegram" ? t('cs_page.channel_telegram') :
                   ch.name === "Email (IMAP/SMTP)" ? t('cs_page.channel_email') :
                   ch.name === "Discord" ? t('cs_page.channel_discord') :
                   ch.name === "Slack" ? t('cs_page.channel_slack') :
                   ch.name === "Zendesk" ? t('cs_page.channel_zendesk') :
                   ch.name === "Intercom" ? t('cs_page.channel_intercom') :
                   ch.name}
                </span>
                {!ch.available && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 ml-1">
                    {t('cs_page.coming_soon')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Section 5 — Pricing */}
        <section
          id="pricing"
          className="max-w-7xl mx-auto px-6 lg:px-12 py-20"
        >
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            {t('cs_page.pricing_title')}
          </h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('cs_page.pricing_subtitle')}
          </p>

          <div className="max-w-lg border border-amber-500/30 rounded-xl p-10 bg-gradient-to-b from-amber-500/[0.03] to-transparent">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-4xl font-bold text-white">{t('cs_page.price')}</span>
            </div>

            <div className="border-t border-zinc-800 pt-6 mb-8">
              <p className="text-sm font-mono uppercase tracking-wider text-zinc-500 mb-4">
                {t('cs_page.included_label')}
              </p>
              <ul className="space-y-3">
                {[
                  t('cs_page.pricing_f1'),
                  t('cs_page.pricing_f2'),
                  t('cs_page.pricing_f3'),
                  t('cs_page.pricing_f4'),
                  t('cs_page.pricing_f5'),
                  t('cs_page.pricing_f6'),
                ].map((item, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 text-sm text-zinc-300"
                  >
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-zinc-500 mb-8">
              {t('cs_page.pricing_note')}
            </p>

            <Link
              to="/signup?agent=customer-support"
              className="block text-center bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              {t('cs_page.activate_button')}
            </Link>
          </div>
        </section>

        {/* Section 6 — Mock conversation */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            {t('cs_page.demo_title')}
          </h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('cs_page.demo_subtitle')}
          </p>

          <div className="max-w-2xl border border-zinc-800 rounded-xl overflow-hidden">
            {/* Chat header */}
            <div className="bg-zinc-900 px-6 py-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Headphones className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">
                    {t('cs_page.agent_name')}
                  </p>
                  <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                    {t('cs_page.agent_status')}
                  </p>
                </div>
              </div>
            </div>

            {/* Chat messages */}
            <div className="p-6 space-y-4">
              {/* Customer message */}
              <div className="flex justify-end">
                <div className="bg-zinc-800 rounded-lg rounded-tr-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('cs_page.demo_msg_1')}
                  </p>
                </div>
              </div>

              {/* Agent message */}
              <div className="flex justify-start">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg rounded-tl-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('cs_page.demo_msg_2')}
                  </p>
                </div>
              </div>

              {/* Customer message */}
              <div className="flex justify-end">
                <div className="bg-zinc-800 rounded-lg rounded-tr-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('cs_page.demo_msg_3')}
                  </p>
                </div>
              </div>

              {/* Agent message */}
              <div className="flex justify-start">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg rounded-tl-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('cs_page.demo_msg_4')}
                  </p>
                </div>
              </div>

              {/* Customer follow-up */}
              <div className="flex justify-end">
                <div className="bg-zinc-800 rounded-lg rounded-tr-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('cs_page.demo_msg_5')}
                  </p>
                </div>
              </div>

              {/* Agent final response */}
              <div className="flex justify-start">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg rounded-tl-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('cs_page.demo_msg_6')}
                  </p>
                </div>
              </div>
            </div>

            {/* Resolution badge */}
            <div className="px-6 pb-6">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-amber-500">
                <Zap className="w-3.5 h-3.5" />
                ⚡ {t('cs_page.resolution_badge')}
              </div>
            </div>
          </div>
        </section>

        {/* Section 6.4 — Testimonials */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            {t('cs_page.testimonials_title')}
          </h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('cs_page.testimonials_subtitle')}
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((item, i) => (
              <div
                key={i}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors flex flex-col"
              >
                <div className="inline-flex items-center gap-2 border border-amber-500/30 rounded-full px-3 py-1 mb-6 self-start">
                  <span className="text-xs font-mono uppercase tracking-wider text-amber-500">
                    {item.metric}
                  </span>
                </div>
                <blockquote className="text-zinc-300 text-sm leading-relaxed flex-1 mb-6">
                  &ldquo;{item.quote}&rdquo;
                </blockquote>
                <div className="flex items-center gap-3 mt-auto pt-4 border-t border-zinc-800">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                    <span className="font-display text-xs text-amber-500">
                      {item.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{item.name}</p>
                    <p className="text-xs text-zinc-500 font-mono">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 6.5 — Mini Case */}
        <section className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="border border-zinc-800 bg-zinc-950/60 p-10 lg:p-14">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">{t('cs_page.case_study_label', {defaultValue: '// CASE STUDY'})}</div>
            <h3 className="font-display text-2xl md:text-3xl uppercase text-white mb-6">{t('cs_page.case_study_title')}</h3>
            <p className="text-zinc-300 text-lg leading-relaxed">
              {t('cs_page.case_study_desc')}
            </p>
            <div className="mt-8 pt-6 border-t border-zinc-800 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <span className="font-display text-xs text-amber-500">{t('cs_page.case_study_badge', {defaultValue: 'CS'})}</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('cs_page.early_adopter')}</span>
            </div>
          </div>
        </section>

        {/* Section 7 — FAQ */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            {t('cs_page.faq_title')}
          </h2>
          <p className="text-zinc-500 mb-10 max-w-lg">
            {t('cs_page.faq_subtitle')}
          </p>

          <div className="max-w-3xl space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem
                key={i}
                question={t(`cs_page.faq_q${i + 1}`)}
                answer={t(`cs_page.faq_a${i + 1}`)}
              />
            ))}
          </div>
        </section>

        {/* Section 7.5 — Agent Skills */}
        <AgentSkillsSection agentSlug="customer-support" agentName="Customer Support" />

        {/* Section 8 — Final CTA */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="border border-amber-500/20 rounded-xl p-12 text-center bg-gradient-to-b from-amber-500/[0.03] to-transparent">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              {t('cs_page.final_cta_title')}
            </h2>
            <p className="text-zinc-500 mb-10 max-w-lg mx-auto">
              {t('cs_page.final_cta_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/signup?agent=customer-support"
                className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                {t('cs_page.activate_cta')}
              </Link>
              <Link
                to="/signup?agent=customer-support&trial=true"
                className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-8 py-3.5 font-mono uppercase tracking-wider text-xs transition-colors"
              >
                {t('cs_page.try_free')}
              </Link>
            </div>
          </div>
        </section>

        {/* Footer verticals nav */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12 border-t border-zinc-800">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-600 mb-6">
            {t('cs_page.other_agents')}
          </p>
          <div className="flex flex-wrap gap-4">
            {[
              { name: t('cs_page.agent_ecommerce'), href: "/marketplace/ecommerce" },
              { name: t('cs_page.agent_realestate'), href: "/marketplace/real-estate" },
              {
                name: t('cs_page.agent_marketing'),
                href: "/marketplace/marketing-agencies",
              },
              { name: t('cs_page.agent_consultancy'), href: "/marketplace/consultancy" },
            ].map((v) => (
              <Link
                key={v.name}
                to={v.href}
                className="text-sm text-zinc-500 hover:text-amber-500 font-mono transition-colors"
              >
                {v.name} →
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
